export function extractConfigObject(htmlString, configName) {
    try {
        const configPattern = new RegExp(
            `(var|let|const)\\s+${configName}\\s*=\\s*(\\{[\\s\\S]*?\\});`,
            "i"
        );
        const match = htmlString.match(configPattern);
        if (match && match[2]) {
            const configStr = match[2];

            // CSP-SAFE: use JSON.parse() instead of new Function().
            // Step 1 — normalise the JS object literal into valid JSON:
            //   • wrap bare keys in double quotes
            //   • convert single-quoted string values to double-quoted
            //   • strip trailing commas before } or ]
            const jsonStr = configStr
                // single-quoted values → double-quoted (simple cases)
                .replace(/'([^']*)'/g, '"$1"')
                // bare object keys → quoted keys
                .replace(/([{,]\s*)([a-zA-Z_$][a-zA-Z0-9_$]*)(\s*:)/g, '$1"$2"$3')
                // trailing commas
                .replace(/,\s*([}\]])/g, '$1');

            try {
                return JSON.parse(jsonStr);
            } catch (_) {
                // JSON.parse failed (template literal / complex expressions inside
                // the config) — return null so callers degrade gracefully.
                return null;
            }
        }
    } catch (error) {
        console.error("Error extracting config object:", error);
    }
    return null;
}

export function updateIDMapping(htmlString, prevID, newID, configFieldNameMain, configObject = null) {
    if (!htmlString || typeof htmlString !== "string") {
        throw new Error("Invalid HTML string provided");
    }
    if (!prevID || typeof prevID !== "string") {
        throw new Error("Invalid prevID provided");
    }
    if (!newID || typeof newID !== "string") {
        throw new Error("Invalid newID provided");
    }

    let workingConfig = configObject;
    let updatedHTML = htmlString;

    if (!configFieldNameMain) {
        throw new Error("ConfigName is required");
    }
    if (!workingConfig) {
        workingConfig = extractConfigObject(htmlString, configFieldNameMain);
        if (!workingConfig) {
            console.warn("Could not extract config object");
        }
    }

    let updatedConfig = null;
    let isValidPath = false;

    if (workingConfig) {
        updatedConfig = JSON.parse(JSON.stringify(workingConfig));
        const pathParts = prevID.split(".");

        if (pathParts.length === 0) {
            console.warn('Invalid prevID format. Expected format: "object.property"');
        } else {
            let currentObj = updatedConfig;
            isValidPath = true;

            for (let i = 0; i < pathParts.length - 1; i++) {
                if (currentObj && typeof currentObj === "object" && pathParts[i] in currentObj) {
                    currentObj = currentObj[pathParts[i]];
                } else {
                    isValidPath = false;
                    console.warn(`Path "${pathParts.slice(0, i + 1).join(".")}" not found in config object`);
                    break;
                }
            }

            if (isValidPath && currentObj && typeof currentObj === "object") {
                const lastKey = pathParts[pathParts.length - 1];
                if (lastKey in currentObj) {
                    currentObj.objDztID = newID;
                } else {
                    console.warn(`Property "${lastKey}" not found at path "${pathParts.slice(0, -1).join(".")}"`);
                    isValidPath = false;
                }
            }
        }

        if (configFieldNameMain && updatedConfig) {
            const configPattern = new RegExp(
                `((var|let|const)\\s+${configFieldNameMain}\\s*=\\s*)(\\{[\\s\\S]*?\\});`,
                "i"
            );
            const configStr = JSON.stringify(updatedConfig, null, 12).replace(/^/gm, "            ");
            updatedHTML = updatedHTML.replace(configPattern, `$1${configStr};`);
        }
    }

    return {
        html: updatedHTML,
        config: updatedConfig,
        configFieldNameMain: configFieldNameMain,
        success: isValidPath,
        message: configFieldNameMain
            ? `Successfully detected and updated config: ${configFieldNameMain}`
            : "HTML updated, but config object not detected",
    };
}

export function extractTemplateField(templateString) {
    if (!templateString || typeof templateString !== "string") {
        return null;
    }
    const match = templateString.match(/(?:\?)\${template.([^}]+)}/);
    return match ? match[1] : null;
}

export function getUpdatedHTML(experience_json, htmlCode) {
    try {
        // CSP-SAFE: replace new Function() / template-literal eval with a regex
        // substitution that finds every ${template.keyPath} expression in the HTML
        // string and replaces it with the corresponding value from experience_json.
        //
        // Supports simple dot-path access, e.g. ${template.hero.title} resolves
        // to experience_json.hero.title.  Unknown paths are left as-is (empty
        // string) so the preview degrades gracefully rather than throwing.
        const obj = { ...experience_json };

        return htmlCode.replace(/\$\{template\.([^}]+)\}/g, (match, keyPath) => {
            try {
                // Walk the key path (supports nested keys like "section.item.text")
                const value = keyPath
                    .split(".")
                    .reduce((acc, key) => (acc != null && typeof acc === "object" ? acc[key] : undefined), obj);

                return value != null ? String(value) : "";
            } catch (_) {
                return "";
            }
        });
    } catch (e) {
        console.error("Error evaluating template HTML:", e);
        return "";
    }
}
